// Wizard steps: Vitej (overview) + Zaklady aplikace (basics).
import { useState } from "react";
import type { AppBasicsPayload } from "../../../shared/app-wizard";
import {
  APP_CATEGORIES,
  APP_COUNTRY_REGIONS,
  APP_CURRENCIES,
  APP_LANGUAGES,
  APP_TEXT_LIMITS
} from "../../../shared/app-wizard";
import {
  CharCount,
  displayName,
  FieldBlock,
  languageFlag,
  SaveBadge,
  useStepSaver,
  type StepProps
} from "./wizard-shared";

export function StepOverview(props: StepProps) {
  return (
    <div className="appwiz-step">
      <header className="appwiz-step-heading">
        <h2>{props.t("appWizard.overview.title")}</h2>
        <p>{props.t("appWizard.overview.copy")}</p>
      </header>
      <ul className="appwiz-overview-list">
        {["stores", "brand", "graphics", "pricing"].map((key) => (
          <li key={key}>{props.t(`appWizard.overview.${key}`)}</li>
        ))}
      </ul>
      <p className="appwiz-overview-note">{props.t("appWizard.overview.parallel")}</p>
      <button className="primary-button compact-button" type="button" onClick={() => props.onOpenStep("basics")}>
        {/* "Začít" next to a 10% progress bar read as a contradiction: the
            welcome step auto-completes. Any real progress = "Pokračovat". */}
        {props.t(props.data.states.some((s) => s.step !== "overview" && s.status !== "todo")
          ? "appWizard.overview.continue"
          : "appWizard.overview.start")}
      </button>
    </div>
  );
}

const EMPTY_BASICS: AppBasicsPayload = {
  stores: { apple: true, google: true },
  appName: "",
  languages: { primary: "", supported: [] },
  defaultCurrency: "",
  primaryCategory: "",
  targetCountries: []
};

export function StepBasics(props: StepProps) {
  const [draft, setDraft] = useState<AppBasicsPayload>(props.data.wizard.steps.basics ?? EMPTY_BASICS);
  const saver = useStepSaver("basics", props.save, props.readOnly);

  const update = (next: AppBasicsPayload) => {
    setDraft(next);
    saver.queue(next as unknown as Record<string, unknown>);
  };

  const toggleStore = (store: "apple" | "google") => {
    const next = { ...draft.stores, [store]: !draft.stores[store] };
    // At least one store must stay selected (reference rule, shown inline).
    if (!next.apple && !next.google) return;
    update({ ...draft, stores: next });
  };

  const toggleSupported = (code: string) => {
    const supported = draft.languages.supported.includes(code)
      ? draft.languages.supported.filter((item) => item !== code)
      : [...draft.languages.supported, code];
    update({ ...draft, languages: { ...draft.languages, supported } });
  };

  const toggleCountry = (code: string) => {
    const targetCountries = draft.targetCountries.includes(code)
      ? draft.targetCountries.filter((item) => item !== code)
      : [...draft.targetCountries, code];
    update({ ...draft, targetCountries });
  };

  const toggleRegion = (countries: readonly string[]) => {
    const allIn = countries.every((code) => draft.targetCountries.includes(code));
    const targetCountries = allIn
      ? draft.targetCountries.filter((code) => !countries.includes(code))
      : [...new Set([...draft.targetCountries, ...countries])];
    update({ ...draft, targetCountries });
  };

  return (
    <div className="appwiz-step">
      <header className="appwiz-step-heading">
        <h2>{props.t("appWizard.basics.title")}</h2>
        <p>{props.t("appWizard.basics.copy")}</p>
        <SaveBadge state={saver.state} t={props.t} />
      </header>

      <FieldBlock label={props.t("appWizard.basics.storesLabel")} hint={props.t("appWizard.basics.storesHint")}>
        <div className="appwiz-chip-row">
          {(["apple", "google"] as const).map((store) => (
            <button
              key={store}
              type="button"
              className={`appwiz-chip${draft.stores[store] ? " on" : ""}`}
              aria-pressed={draft.stores[store]}
              disabled={props.readOnly}
              onClick={() => toggleStore(store)}
            >
              {store === "apple" ? (
                <><svg className="appwiz-chip-icon" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M17.05 12.54c-.03-2.5 2.04-3.7 2.13-3.76-1.16-1.7-2.97-1.93-3.61-1.96-1.54-.16-3 .9-3.78.9-.78 0-1.98-.88-3.26-.86-1.68.03-3.22.98-4.08 2.48-1.74 3.02-.44 7.5 1.25 9.95.83 1.2 1.82 2.55 3.12 2.5 1.25-.05 1.72-.8 3.23-.8 1.5 0 1.93.8 3.25.78 1.35-.02 2.2-1.22 3.02-2.43.95-1.39 1.34-2.74 1.36-2.81-.03-.01-2.6-1-2.63-3.99Zm-2.48-7.34c.69-.83 1.15-1.99 1.02-3.14-.99.04-2.18.66-2.89 1.49-.63.73-1.19 1.91-1.04 3.04 1.1.09 2.23-.56 2.91-1.39Z"/></svg><span>{"App Store"}</span></>
              ) : (
                <><svg className="appwiz-chip-icon" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M3.6 1.8c-.36.2-.6.58-.6 1.08v18.24c0 .5.24.88.6 1.08l10.02-10.2L3.6 1.8Zm11.13 9.07 2.63-2.68-11.6-6.63 8.97 9.31Zm3.87 2.12 2.7-1.54c.94-.54.94-1.42 0-1.96l-2.7-1.55-2.9 2.53 2.9 2.52Zm-12.84 8.6 11.6-6.62-2.63-2.69-8.97 9.31Z"/></svg><span>{"Google Play"}</span></>
              )}
            </button>
          ))}
        </div>
        <small className="appwiz-inline-rule">{props.t("appWizard.basics.storesRule")}</small>
      </FieldBlock>

      <FieldBlock label={props.t("appWizard.basics.nameLabel")} hint={props.t("appWizard.basics.nameHint")}>
        <span className="appwiz-locale-input appwiz-name-input">
          <input
            type="text"
            value={draft.appName}
            maxLength={APP_TEXT_LIMITS.appName}
            readOnly={props.readOnly}
            aria-label={props.t("appWizard.basics.nameLabel")}
            onChange={(event) => update({ ...draft, appName: event.target.value })}
          />
          <CharCount value={draft.appName} max={APP_TEXT_LIMITS.appName} />
        </span>
      </FieldBlock>

      <FieldBlock label={props.t("appWizard.basics.primaryLanguageLabel")} hint={props.t("appWizard.basics.primaryLanguageHint")}>
        <select
          value={draft.languages.primary}
          disabled={props.readOnly}
          aria-label={props.t("appWizard.basics.primaryLanguageLabel")}
          onChange={(event) => update({
            ...draft,
            languages: {
              primary: event.target.value,
              supported: draft.languages.supported.filter((item) => item !== event.target.value)
            }
          })}
        >
          <option value="">{props.t("appWizard.basics.primaryLanguagePlaceholder")}</option>
          {APP_LANGUAGES.map((code) => (
            <option key={code} value={code}>{languageFlag(code)} {displayName(props.locale, "language", code)}</option>
          ))}
        </select>
      </FieldBlock>

      <FieldBlock label={props.t("appWizard.basics.moreLanguagesLabel")} hint={props.t("appWizard.basics.moreLanguagesHint")}>
        <div className="appwiz-chip-row">
          {APP_LANGUAGES.filter((code) => code !== draft.languages.primary).map((code) => (
            <button
              key={code}
              type="button"
              className={`appwiz-chip${draft.languages.supported.includes(code) ? " on" : ""}`}
              aria-pressed={draft.languages.supported.includes(code)}
              disabled={props.readOnly}
              onClick={() => toggleSupported(code)}
            >
              {languageFlag(code)} {displayName(props.locale, "language", code)}
            </button>
          ))}
        </div>
      </FieldBlock>

      <FieldBlock label={props.t("appWizard.basics.currencyLabel")} hint={props.t("appWizard.basics.currencyHint")}>
        <select
          value={draft.defaultCurrency}
          disabled={props.readOnly}
          aria-label={props.t("appWizard.basics.currencyLabel")}
          onChange={(event) => update({ ...draft, defaultCurrency: event.target.value })}
        >
          <option value="">{props.t("appWizard.basics.currencyPlaceholder")}</option>
          {APP_CURRENCIES.map((code) => <option key={code} value={code}>{code}</option>)}
        </select>
      </FieldBlock>

      <FieldBlock label={props.t("appWizard.basics.categoryLabel")} hint={props.t("appWizard.basics.categoryHint")}>
        <select
          value={draft.primaryCategory}
          disabled={props.readOnly}
          aria-label={props.t("appWizard.basics.categoryLabel")}
          onChange={(event) => update({ ...draft, primaryCategory: event.target.value })}
        >
          <option value="">{props.t("appWizard.basics.categoryPlaceholder")}</option>
          {APP_CATEGORIES.map((key) => (
            <option key={key} value={key}>{props.t(`appWizard.categories.${key}`)}</option>
          ))}
        </select>
      </FieldBlock>

      <FieldBlock label={props.t("appWizard.basics.countriesLabel")} hint={props.t("appWizard.basics.countriesHint")}>
        {APP_COUNTRY_REGIONS.map((region) => {
          const allIn = region.countries.every((code) => draft.targetCountries.includes(code));
          const selectedCount = region.countries.filter((code) => draft.targetCountries.includes(code)).length;
          return (
            <details className="appwiz-region" key={region.key} open={selectedCount > 0 && !allIn}>
              <summary>
                <span>{props.t(`appWizard.regions.${region.key}`)}</span>
                <span className="appwiz-region-meta">
                  <small>{selectedCount}/{region.countries.length}</small>
                  {!props.readOnly && (
                    <button className="text-button" type="button" onClick={(event) => { event.preventDefault(); toggleRegion(region.countries); }}>
                      {props.t(allIn ? "appWizard.basics.regionNone" : "appWizard.basics.regionAll")}
                    </button>
                  )}
                </span>
              </summary>
              <div className="appwiz-chip-row">
                {region.countries.map((code) => (
                  <button
                    key={code}
                    type="button"
                    className={`appwiz-chip small${draft.targetCountries.includes(code) ? " on" : ""}`}
                    aria-pressed={draft.targetCountries.includes(code)}
                    disabled={props.readOnly}
                    onClick={() => toggleCountry(code)}
                  >
                    {displayName(props.locale, "region", code)}
                  </button>
                ))}
              </div>
            </details>
          );
        })}
      </FieldBlock>
    </div>
  );
}
